// Live store for the suggestion taxonomy (genre/mood/instrument families, popular sets,
// descriptions) that drives the picker's grouping + presentation.
//
// The bundled `suggestionTaxonomy.json` is the synchronous default, so the editor renders
// correct menus on first paint and offline. At startup the app/extension fetches the server's
// copy (loadSuggestionTaxonomy in api.ts) and calls applyTaxonomy() to overlay it — so adding
// genres/moods/descriptions ships via a server deploy, with NO extension rebuild required.
//
// NOTE: this overlay affects PRESENTATION only. Audio sample-key resolution (sampleExamples.ts)
// reads the bundled family files directly, since clips are bundled assets — a server-added genre
// simply shows no play button on an older build until the next asset ship.
import bundled from './suggestionTaxonomy.json'

export interface TaxonomyEntry {
  value: string
  key?: string
  desc?: string
}
export interface TaxonomyFamily {
  key: string
  label: string
  entries: TaxonomyEntry[]
}
export interface DimensionTaxonomy {
  popular: string[]
  families: TaxonomyFamily[]
}
export interface Taxonomy {
  genre: DimensionTaxonomy
  mood: DimensionTaxonomy
  instrumentation: DimensionTaxonomy
  valueDescriptions: Record<string, string>
}

let current = bundled as unknown as Taxonomy
let version = 0
const listeners = new Set<() => void>()

export function getTaxonomy(): Taxonomy {
  return current
}

// Bumps on every applyTaxonomy(); consumers cache derived data keyed by this and rebuild on change.
export function getTaxonomyVersion(): number {
  return version
}

export function subscribeTaxonomy(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

// Overlay a fetched taxonomy. Guards against an empty/malformed payload so a bad response can
// never wipe the menus — we keep the last good taxonomy (worst case, the bundled default).
export function applyTaxonomy(next: Taxonomy | null | undefined): void {
  if (!next?.genre?.families?.length || !next?.mood?.families?.length) return
  current = next
  version++
  listeners.forEach((l) => l())
}
